/* eslint-disable max-lines-per-function */
import type { Space } from '@mail/cross-sizes-utils';
import { SECOND_MS } from '@mail-core/std';
import { xray } from 'lib/xray';
import {
    fastCleanRetryCount,
    fastCleanTimeoutSec,
    quotaCleanerSafeHelpLink,
} from 'reactApp/appHelpers/featuresHelpers/features/quotaCleaner';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { loadUserQuotaFailure, loadUserQuotaRequest, loadUserQuotaSuccess } from 'reactApp/modules/userQuota/userQuota.module';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { mergeFastCleanResults } from 'reactApp/modules/userQuotaCleaner/helpers/mergeFastCleanResults';
import {
    fastClean,
    fastCleanDropCleanState,
    fastCleanFlowEnd,
    loadUserQuotaCleanerList,
    loadUserQuotaCleanerListError,
    loadUserQuotaCleanerListSuccess,
    setFastCleanInfo,
    setFastCleanInfoIsLoaded,
} from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.actions';
import { quotaCleanerDebugLogger } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.constants';
import { FastCleanSelectors, QuotaCleanerSelectors } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import type { QuotaFastCleanerState } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.types';
import { renderFastCleanConfirmModal } from 'reactApp/sections/QuotaLanding/FastCleaner/FastCleanerConfirm/FastCleanerConfirm.helpers';
import { renderFastCleanLoadingModal } from 'reactApp/sections/QuotaLanding/FastCleaner/FastCleanerLoadingModal/FastCleanerLoadingModal.helpers';
import { renderFastCleanModal } from 'reactApp/sections/QuotaLanding/FastCleaner/FastCleanerModal/FastCleanerModal.helpers';
import { renderFastCleanSafeModal } from 'reactApp/sections/QuotaLanding/FastCleaner/FastCleanerSafeModal/FastCleanerSafeModal.helpers';
import { store as reduxStore } from 'reactApp/store';
import { sendDwh } from 'reactApp/utils/ga';
import { getPlatformInfo } from 'reactApp/utils/gaHelpers';
import { opener } from 'reactApp/utils/opener';
import { channel } from 'redux-saga';
import { call, delay, put, select, take } from 'redux-saga/effects';
import { race } from 'typed-redux-saga';

import { type FastCleanProcessReturnValueType, type FastCleanResultType, fastCleanProcess } from './fastCleanProcess.saga';

const MINIMAL_LOADING_TIME = 700;

const sendNotifyRadar = (name: string) => {
    sendDwh({
        eventCategory: 'notify',
        action: 'show',
        dwh_enabled: true,
        dwh_enabled_fields: [['name', 'offer_source', 'platform']],
        dwhData: {
            name,
            platform: getPlatformInfo().join('_'),
            offer_source: QuotaCleanerSelectors.getEntryPoint(reduxStore.getState()) || 'none',
        },
    });
};

function* safeInfoBranch() {
    const flowChannel = yield call(channel);
    let flowStepValue = '';

    const { cloudRemoveSize, cloudSafeSize } = yield select(FastCleanSelectors.getCloud);
    const hasFiles = cloudSafeSize + cloudRemoveSize > 0;

    // ветка модалки инфо о сейфе
    yield call(renderFastCleanSafeModal, {
        type: 'info',
        coverType: hasFiles ? 'files' : 'letters',
        onPrimaryClick: () => {
            // по кнопке Вернуться к очистке – заново бросаем экшен fastClean
            flowChannel.put('return');
        },
        closeOnPrimaryClick: true,
        onSecondaryClick: () => {
            flowChannel.put('help');
        },
        // здесь по клику не закрываем модалку,
        // так как открываем хелп в другом окне и юзер может вернуться
        closeOnSecondaryClick: false,
        onCancel: () => {
            flowChannel.put('close');
        },
    });

    // на хелп можем жать много раз, поэтому в цикле
    while (true) {
        flowStepValue = yield take(flowChannel);
        if (flowStepValue === 'help') {
            // ведем на хелп сейфа
            yield call(opener, quotaCleanerSafeHelpLink);
        } else {
            break;
        }
    }

    if (flowStepValue === 'return') {
        yield put(fastClean());
    }
    return;
}

export function* fastCleanerFlow() {
    const flowChannel = yield call(channel);
    let flowStepValue = '';

    const { cloudRemoveSize, cloudSafeSize } = yield select(FastCleanSelectors.getCloud);
    const hasFiles = cloudSafeSize + cloudRemoveSize > 0;

    // Открываем модалку быстрой очистки
    // предполагается, что на primaryClick модалка закроется сама
    yield call(renderFastCleanModal, {
        onPrimaryClick: () => {
            flowChannel.put('clean');
        },
        closeOnPrimaryClick: true,
        onLinkClick: () => {
            flowChannel.put('safe');
        },
        closeOnLinkClick: true,
        onCancel: () => {
            flowChannel.put('close');
        },
    });

    // грузим заранее модалки из другого бандла, чтобы не было подвисаний после нажатия
    import(/* webpackChunkName: "Popups" */ 'reactApp/sections/QuotaLanding/FastCleaner/FastCleanerConfirm/FastCleanerConfirm');

    flowStepValue = yield take(flowChannel);

    if (flowStepValue === 'safe') {
        // тупиковая ветка с инфой о сейфе – вернуться к началу флоу можно, но новым экшеном
        return yield safeInfoBranch();
    }

    if (flowStepValue !== 'clean') {
        return;
    }

    // Открываем модалку подтверждения
    yield call(renderFastCleanConfirmModal, {
        onPrimaryClick: () => {
            flowChannel.put('confirm');
        },
        closeOnPrimaryClick: true,
        onLinkClick: () => {
            flowChannel.put('safe');
        },
        closeOnLinkClick: true,
        onCancel: () => {
            flowChannel.put('close');
        },
    });

    flowStepValue = yield take(flowChannel);

    if (flowStepValue === 'safe') {
        return yield safeInfoBranch();
    }

    if (flowStepValue !== 'confirm') {
        return;
    }

    const { fastCleanGroups: groups }: QuotaFastCleanerState = yield select(FastCleanSelectors.getState);

    // Открываем модалку загрузки
    const closeFastCleanLoadingModal = yield call(renderFastCleanLoadingModal);

    const { original: overquotaBytesBefore }: Space = yield select(UserQuotaSelectors.getOverquotaBytes);

    const expectedTotalCleared: number = yield select(FastCleanSelectors.getExpectedCleared);

    let cleanResult: FastCleanResultType | 'timeout' | '' = '';
    let isSuccessClean = false;
    let cleanStatus = false;
    let overquotaBytesAfter = overquotaBytesBefore;
    const retryCount = fastCleanRetryCount;
    let currentTry = 1;

    const clearStartTime = yield call(Date.now);
    for (; currentTry < retryCount + 1; ++currentTry) {
        const tryClearStartTime = yield call(Date.now);

        if (cleanStatus) {
            yield put(fastCleanDropCleanState());
        }

        // Очистка, статус загрузки в редакс стейте
        const { fastCleanResult, timeout }: { fastCleanResult: FastCleanProcessReturnValueType; timeout: any } = yield race({
            fastCleanResult: call(fastCleanProcess, { groups, overquotaBytes: overquotaBytesAfter }),
            timeout: delay(fastCleanTimeoutSec * SECOND_MS),
        });

        let tryCleanResult: FastCleanResultType | 'timeout' | '' = '';

        if (fastCleanResult) {
            cleanStatus = fastCleanResult.status;
            tryCleanResult = fastCleanResult.result;
        } else if (timeout) {
            cleanStatus = false;
            tryCleanResult = 'timeout';
            xray.send('quota-fast-cln-timeout', {
                dwh_enabled: true,
                dwh_enabled_fields: [['try', 'platform']],
                dwh: { try: currentTry, platform: getPlatformInfo().join('_') },
            });
        }

        cleanResult = mergeFastCleanResults(cleanResult, tryCleanResult);

        yield put(loadUserQuotaRequest());

        yield race({
            success: take(loadUserQuotaSuccess.toString()),
            fail: take(loadUserQuotaFailure.toString()),
            timeout: delay(5 * SECOND_MS),
        });

        const tryClearEndTime = yield call(Date.now);
        const tryClearTime = tryClearEndTime - tryClearStartTime;

        const overquotaSpaceAfterTry: Space = yield select(UserQuotaSelectors.getOverquotaBytes);
        const overquotaBytesBeforeTry = overquotaBytesAfter;
        overquotaBytesAfter = overquotaSpaceAfterTry?.original || 0;
        isSuccessClean = overquotaBytesAfter <= 0;

        const dwh = {
            status: cleanStatus,
            exit: isSuccessClean,
            clear_time: Math.ceil(tryClearTime / SECOND_MS),
            result: tryCleanResult,

            overquota_before: overquotaBytesBeforeTry,
            overquota_after: overquotaBytesAfter,

            platform: getPlatformInfo().join('_'),
        };

        const dwh_enabled_fields: (keyof typeof dwh)[][] = [['status', 'exit', 'result', 'clear_time', 'platform']];

        xray.send(`quota-fast-cln-try-${currentTry}`, {
            dwh_enabled: true,
            dwh_enabled_fields,
            dwh,
        });

        if (isSuccessClean) {
            break;
        }
    }

    const clearEndTime = yield call(Date.now);
    const clearTime = clearEndTime - clearStartTime;

    const { cleared }: QuotaFastCleanerState = yield select(FastCleanSelectors.getState);

    const clearTimeSec = Math.ceil(clearTime / SECOND_MS);
    if (clearTimeSec > 25 * SECOND_MS) {
        quotaCleanerDebugLogger.error('loading time', clearTimeSec);
    } else {
        quotaCleanerDebugLogger.ok('loading time', clearTimeSec);
    }

    const dwh = {
        status: cleanStatus,
        exit: overquotaBytesAfter < 0,
        clear_time: clearTimeSec,
        try: currentTry,

        total_cleared: cleared.total,
        overquota_before: overquotaBytesBefore,
        overquota_after: overquotaBytesAfter,
        expected: expectedTotalCleared,

        platform: getPlatformInfo().join('_'),
    };

    const dwh_enabled_fields: (keyof typeof dwh)[][] = [
        ['status', 'exit', 'try', 'platform'],
        ['clear_time', 'platform'],
    ];

    xray.send('quota-fast-cln-info', {
        dwh_enabled: true,
        dwh_enabled_fields,
        dwh,
    });

    // если быстрая очистка реально БЫСТРАЯ, то
    // пусть модалка загрузки чуть повисит, чтоб не мигала
    if (clearTime < MINIMAL_LOADING_TIME && isSuccessClean) {
        yield delay(MINIMAL_LOADING_TIME - clearTime);
    }

    // Закрываем модалку загрузки
    yield call(closeFastCleanLoadingModal);

    if (isSuccessClean && cleanResult === 'remove_and_safe') {
        // Показываем модалку успеха
        yield call(renderFastCleanSafeModal, {
            type: 'success',
            coverType: hasFiles ? 'files' : 'letters',
            onPrimaryClick: () => {
                flowChannel.put('ok');
            },
            closeOnPrimaryClick: true,
            onSecondaryClick: () => {
                flowChannel.put('help');
            },
            // не закрываем модалку по клику
            closeOnSecondaryClick: false,
            onCancel: () => {
                flowChannel.put('close');
            },
        });

        // на хелп можем жать много раз, поэтому в цикле
        while (true) {
            flowStepValue = yield take(flowChannel);
            if (flowStepValue === 'help') {
                // ведем на хелп сейфа
                yield call(opener, quotaCleanerSafeHelpLink);
            } else {
                break;
            }
        }
    }

    if (isSuccessClean) {
        yield put(
            showSnackbarAction({
                type: SnackbarTypes.success,
                id: 'fast_clean_success',
                text: 'Место освободилось — ограничения сняты',
                closable: true,
            })
        );
        sendNotifyRadar('fast_clean_success');
    } else {
        // разбираемся, какая ошибка и чето делаем
        // тут можно по cleanResult решить, что показывать/говорить юзеру
        yield put(
            showSnackbarAction({
                type: SnackbarTypes.failure,
                id: 'fast_clean_fail',
                text: 'Произошла ошибка',
                closable: true,
            })
        );
        sendNotifyRadar('fast_clean_error');
    }

    // переходим к блоку квоты
    yield put(fastCleanFlowEnd());

    // обновляем чистилку
    yield put(setFastCleanInfoIsLoaded(false));
    yield put(loadUserQuotaCleanerList());

    yield race({
        success: take(loadUserQuotaCleanerListSuccess.toString()),
        fail: take(loadUserQuotaCleanerListError.toString()),
        timeout: delay(5000),
    });

    // обновляем быструю чистилку
    // если overquotaBytesAfter < 0 (вышли из оверквоты), то после обновления кнопка пропадет
    yield put(setFastCleanInfo({ overquotaBytes: overquotaBytesAfter }));
}
